// Transformers.js Embedding Adapter
// Browser-native embedding using Hugging Face Transformers.js
//
// This adapter runs entirely in the browser using WebGPU/WASM
// No server required - models are downloaded and cached locally

// Default model - multilingual, supports 100+ languages including Chinese
const DEFAULT_MODEL = 'Xenova/multilingual-e5-small'

// Model loading states
const LoadState = {
  NOT_LOADED: 'not_loaded',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
}

// Global state for model loading
const modelState = new Map() // model -> { state, progress, error, pipeline }

// Dynamic import to avoid bundling issues
let transformersModule = null

/**
 * Load the transformers module dynamically
 * @returns {Promise<Object>} Transformers module
 */
async function loadTransformers() {
  if (!transformersModule) {
    transformersModule = await import('@xenova/transformers')
  }
  return transformersModule
}

/**
 * Get model state
 * @param {string} model - Model name
 * @returns {Object} { state, progress, error }
 */
function getModelState(model) {
  if (!modelState.has(model)) {
    modelState.set(model, {
      state: LoadState.NOT_LOADED,
      progress: null,
      error: null,
      pipeline: null,
    })
  }
  return modelState.get(model)
}

/**
 * Load a model with progress tracking
 * @param {string} model - Model name
 * @param {Object} options - Options
 * @param {Function} options.onProgress - Progress callback ({ status, file, loaded, total, progress })
 * @param {Function} options.onStateChange - State change callback ({ state, model })
 * @returns {Promise<Function>} Pipeline function
 */
async function loadModel(model, options = {}) {
  const { onProgress, onStateChange } = options
  const state = getModelState(model)

  // Already loaded
  if (state.state === LoadState.LOADED && state.pipeline) {
    return state.pipeline
  }

  // Currently loading - wait for it
  if (state.state === LoadState.LOADING) {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        const currentState = getModelState(model)
        if (currentState.state === LoadState.LOADED) {
          clearInterval(checkInterval)
          resolve(currentState.pipeline)
        } else if (currentState.state === LoadState.ERROR) {
          clearInterval(checkInterval)
          reject(new Error(currentState.error))
        }
      }, 100)
    })
  }

  // Start loading
  state.state = LoadState.LOADING
  state.progress = { status: 'initializing', progress: 0 }
  onStateChange?.({ state: LoadState.LOADING, model })

  try {
    const { pipeline, env } = await loadTransformers()

    // Configure environment
    env.allowLocalModels = false

    // Create pipeline with progress callback
    const extractor = await pipeline('feature-extraction', model, {
      progress_callback: (progress) => {
        // Normalize progress info
        const progressInfo = {
          status: progress.status,
          file: progress.file,
          loaded: progress.loaded,
          total: progress.total,
          progress: progress.total ? Math.round((progress.loaded / progress.total) * 100) : 0,
        }

        state.progress = progressInfo
        onProgress?.(progressInfo)
      },
    })

    state.state = LoadState.LOADED
    state.pipeline = extractor
    state.progress = { status: 'ready', progress: 100 }
    onStateChange?.({ state: LoadState.LOADED, model })

    return extractor
  } catch (error) {
    state.state = LoadState.ERROR
    state.error = error.message
    onStateChange?.({ state: LoadState.ERROR, model, error: error.message })
    throw error
  }
}

/**
 * Create Transformers.js embedding adapter
 * @param {Function} _httpRequest - Not used (kept for API compatibility)
 * @returns {Object} Transformers adapter
 */
export function createTransformersAdapter(_httpRequest) {
  /**
   * Generate embeddings using Transformers.js
   * @param {Object} params
   * @param {string|string[]} params.texts - Text(s) to embed
   * @param {string} params.model - Model name (default: all-MiniLM-L6-v2)
   * @param {Object} params.config - Additional config
   * @param {Function} params.config.onProgress - Progress callback for model loading
   * @returns {Promise<number[][]>} Array of embedding vectors
   */
  const embed = async ({ texts, model = DEFAULT_MODEL, config = {} }) => {
    const { onProgress, onStateChange } = config
    const extractor = await loadModel(model, { onProgress, onStateChange })

    // Normalize input to array
    const inputTexts = typeof texts === 'string' ? [texts] : texts

    // Generate embeddings for each text
    const embeddings = []

    for (const text of inputTexts) {
      const output = await extractor(text, {
        pooling: 'mean',
        normalize: true,
      })

      // Convert to regular array
      embeddings.push(Array.from(output.data))
    }

    return embeddings
  }

  /**
   * Check if Transformers.js is available
   * @returns {Promise<boolean>}
   */
  const isAvailable = async () => {
    try {
      await loadTransformers()
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Get model info and loading state
   * @param {Object} params
   * @param {string} params.model - Model name
   * @returns {Promise<Object>}
   */
  const info = async ({ model = DEFAULT_MODEL } = {}) => {
    const state = getModelState(model)
    return {
      provider: 'transformers',
      model,
      runtime: 'browser',
      backend: typeof navigator !== 'undefined' && navigator.gpu ? 'webgpu' : 'wasm',
      loadState: state.state,
      progress: state.progress,
    }
  }

  /**
   * Preload a model with progress tracking
   * Call this early to avoid blocking user interactions
   *
   * @param {Object} options
   * @param {string} options.model - Model name
   * @param {Function} options.onProgress - Progress callback ({ status, file, loaded, total, progress })
   * @param {Function} options.onStateChange - State change callback ({ state, model })
   * @returns {Promise<void>}
   *
   * @example
   * // Preload with progress UI
   * await preload({
   *   onProgress: ({ status, progress, file }) => {
   *     if (status === 'downloading') {
   *       console.log(`Downloading ${file}: ${progress}%`)
   *     }
   *   },
   *   onStateChange: ({ state }) => {
   *     if (state === 'loaded') console.log('Model ready!')
   *   }
   * })
   */
  const preload = async (options = {}) => {
    const { model = DEFAULT_MODEL, onProgress, onStateChange } = options
    await loadModel(model, { onProgress, onStateChange })
  }

  /**
   * Check if a model is already loaded
   * @param {string} model - Model name
   * @returns {boolean}
   */
  const isModelLoaded = (model = DEFAULT_MODEL) => {
    const state = getModelState(model)
    return state.state === LoadState.LOADED
  }

  /**
   * Get current loading state for a model
   * @param {string} model - Model name
   * @returns {Object} { state, progress, error }
   */
  const getLoadState = (model = DEFAULT_MODEL) => {
    const state = getModelState(model)
    return {
      state: state.state,
      progress: state.progress,
      error: state.error,
    }
  }

  /**
   * Clear the model cache
   * @param {string} model - Optional specific model to clear, or all if not specified
   */
  const clearCache = (model) => {
    if (model) {
      modelState.delete(model)
    } else {
      modelState.clear()
    }
  }

  return {
    embed,
    isAvailable,
    info,
    preload,
    isModelLoaded,
    getLoadState,
    clearCache,
  }
}

// Export constants
export { DEFAULT_MODEL, LoadState }
